/*
* === TemplateManagerPage.js (Final - With Clear Ownership & Duplicate) ===
* Features:
* - Clear Accordion Headers: "{CompanyName} – Templates by {AdminName}"
* - Rich Text Editor (ReactQuill)
* - Smart Variable Picker & Live Preview
* - Duplicate Template to Another Company (SuperUser Only)
*/
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { 
    Container, Typography, Card, Box, Alert, Button, Grid, Paper, 
    CircularProgress, IconButton, Dialog, DialogTitle, DialogContent, 
    DialogActions, TextField, List, ListItem, ListItemText, ListItemSecondaryAction,
    Divider, Chip, Tabs, Tab, Tooltip, useTheme, FormControl, InputLabel, Select, MenuItem, DialogContentText,
    Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

// Icons
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CodeIcon from '@mui/icons-material/Code';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import PublicIcon from '@mui/icons-material/Public'; // Icon for System Templates

// Rich Text Editor
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// --- Constants: Available Variables ---
const AVAILABLE_VARIABLES = [
    { label: 'Applicant Name', code: '{{FullName}}' },
    { label: 'Bursary Name', code: '{{BursaryName}}' },
    { label: 'Application ID', code: '{{ApplicationId}}' },
    { label: 'Status', code: '{{Status}}' },
    { label: 'Feedback', code: '{{Feedback}}' },
];

// --- Delete Confirmation Modal ---
const DeleteConfirmModal = ({ open, onClose, onConfirm, templateName }) => {
    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
                <WarningAmberIcon sx={{ mr: 1 }} /> Confirm Deletion
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Are you sure you want to delete the template: <strong>"{templateName}"</strong>?
                </DialogContentText>
                <Alert severity="warning" sx={{ mt: 2 }}>This action cannot be undone.</Alert>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={onConfirm} color="error" variant="contained">Delete</Button>
            </DialogActions>
        </Dialog>
    );
};

// --- Duplicate Confirmation Modal ---
const DuplicateConfirmModal = ({ open, onClose, onConfirm, templateName, companies }) => {
    const [targetCompanyId, setTargetCompanyId] = useState('');
    const [error, setError] = useState('');

    const handleConfirm = () => {
        if (!targetCompanyId) {
            setError('Please select a target company.');
            return;
        }
        onConfirm(targetCompanyId);
    };

    useEffect(() => {
        if (open) {
            setTargetCompanyId('');
            setError('');
        }
    }, [open]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
                <ContentCopyIcon color="primary" sx={{ mr: 1 }} /> Duplicate Template
            </DialogTitle>
            <DialogContent>
                <DialogContentText gutterBottom>
                    Copy <strong>"{templateName}"</strong> to another company?
                </DialogContentText>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                
                <FormControl fullWidth margin="dense">
                    <InputLabel>Select Target Company</InputLabel>
                    <Select
                        value={targetCompanyId}
                        label="Select Target Company"
                        onChange={(e) => setTargetCompanyId(e.target.value)}
                    >
                        {companies.length > 0 ? (
                            companies.map((comp) => (
                                <MenuItem key={comp.Id} value={comp.Id}>
                                    {comp.CompanyName}
                                </MenuItem>
                            ))
                        ) : (
                            <MenuItem disabled>No companies available</MenuItem>
                        )}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleConfirm} variant="contained" color="primary">
                    Duplicate
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const TemplateManagerPage = () => {
    const [templates, setTemplates] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { token, user } = useAuth();
    const theme = useTheme();
    const isSuperUser = user?.role === 'SuperUser';

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [formError, setFormError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const [editTab, setEditTab] = useState(0); 
    const quillRef = useRef(null);

    const fetchTemplates = useCallback(async () => {
        try {
            setLoading(true);
            // Note: The backend /admin/templates route should join with Users (for FullName) and Companies (for CompanyName)
            const response = await api.get('/admin/templates', { headers: { Authorization: `Bearer ${token}` } });
            setTemplates(response.data);
        } catch (err) {
            console.error("Failed to fetch templates:", err);
            setError('Failed to fetch templates.');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchTemplates();
        
        const fetchCompanies = async () => {
            if (user?.role === 'SuperUser') {
                try {
                    const res = await api.get('/admin/companies', { headers: { Authorization: `Bearer ${token}` } });
                    setCompanies(res.data || []); 
                } catch (err) {
                    console.error("Failed to fetch companies for duplication:", err);
                }
            }
        };
        
        fetchCompanies();
    }, [fetchTemplates, token, user?.role]);

     // --- Grouping & Filtering Logic ---
    const groupedTemplates = useMemo(() => {
        let filtered = templates;
        
        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            filtered = templates.filter(t => 
                t.TemplateName.toLowerCase().includes(lower) || 
                (t.CompanyName && t.CompanyName.toLowerCase().includes(lower))
            );
        }

        // If Admin, show only their templates in a single group
        if (!isSuperUser) {
            return { 'My Templates': filtered };
        }

        // If SuperUser, group by Company
        return filtered.reduce((groups, t) => {
            // Determine the Header Key based on Company & Creator
            let key;
            if (t.CompanyId) {
                // It's a Company Template
                const companyName = t.CompanyName || 'Unknown Company';
                const creatorName = t.CreatorName || 'Unknown Admin'; 
                // Format: "{CompanyName} – Templates by {AdminName}"
                key = `${companyName} – Templates by ${creatorName}`;
            } else {
                // It's a Global/System Template (No CompanyId)
                const creatorName = t.CreatorName || 'SuperUser';
                key = `System Templates – Global (by ${creatorName})`;
            }
            
            if (!groups[key]) groups[key] = [];
            groups[key].push(t);
            return groups;
        }, {});
    }, [templates, searchTerm, isSuperUser]);


    const handleOpenModal = (template = null) => {
        setSelectedTemplate(template ? { ...template } : { TemplateName: '', Subject: '', Body: '' });
        setEditTab(0);
        setIsModalOpen(true);
        setFormError('');
    };

    const handleCloseModal = () => {
        if (isSubmitting) return; 
        setIsModalOpen(false);
        setSelectedTemplate(null);
    };

    const handleSave = async () => {
        if (!selectedTemplate?.TemplateName || !selectedTemplate?.Subject || !selectedTemplate?.Body) {
            setFormError('All fields are required.');
            return;
        }
        
        setIsSubmitting(true);
        setFormError('');

        const isNew = !selectedTemplate.Id;
        const url = isNew ? '/admin/templates' : `/admin/templates/${selectedTemplate.Id}`;
        const method = isNew ? 'post' : 'put';

        try {
            await api[method](url, selectedTemplate, { headers: { Authorization: `Bearer ${token}` } });
            fetchTemplates();
            handleCloseModal(); 
        } catch (err) {
            setFormError(err.response?.data?.message || 'Failed to save template.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOpenDeleteConfirm = (template) => {
        setSelectedTemplate(template);
        setIsDeleteModalOpen(true);
    };

    const handleCloseDeleteConfirm = () => {
        setSelectedTemplate(null);
        setIsDeleteModalOpen(false);
    };

    const handleConfirmDelete = async () => {
        if (!selectedTemplate) return;
        try {
            await api.delete(`/admin/templates/${selectedTemplate.Id}`, { headers: { Authorization: `Bearer ${token}` } });
            fetchTemplates();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete template.');
        } finally {
            handleCloseDeleteConfirm();
        }
    };

    // --- Duplicate Logic ---
    const handleOpenDuplicateModal = (template) => {
        setSelectedTemplate(template);
        setIsDuplicateModalOpen(true);
    };

    const handleCloseDuplicateModal = () => {
        setIsDuplicateModalOpen(false);
        setSelectedTemplate(null);
    };

    const handleConfirmDuplicate = async (targetCompanyId) => {
        if (!selectedTemplate || !targetCompanyId) return;
        
        try {
            await api.post(`/admin/templates/${selectedTemplate.Id}/duplicate`, 
                { targetCompanyId }, 
                { headers: { Authorization: `Bearer ${token}` } }
            );
            alert(`Template duplicated successfully!`);
            fetchTemplates();
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to duplicate template.');
        } finally {
            handleCloseDuplicateModal();
        }
    };

    const insertVariable = (code) => {
        if (quillRef.current) {
            const editor = quillRef.current.getEditor();
            const cursorPosition = editor.getSelection(true)?.index || 0;
            editor.insertText(cursorPosition, code);
        } else {
            setSelectedTemplate(prev => ({ ...prev, Body: (prev.Body || '') + code }));
        }
    };

    const getPreviewHtml = (body) => {
        if (!body) return '';
        return body
            .replace(/{{FullName}}/g, '<span style="color:blue; background:#e3f2fd; padding:0 4px; border-radius:4px">John Doe</span>')
            .replace(/{{BursaryName}}/g, '<span style="color:green; background:#e8f5e9; padding:0 4px; border-radius:4px">Engineering Fund 2025</span>')
            .replace(/{{ApplicationId}}/g, '<span style="color:purple; background:#f3e5f5; padding:0 4px; border-radius:4px">#1054</span>')
            .replace(/{{Status}}/g, '<strong>Approved</strong>')
            .replace(/{{Feedback}}/g, '<em>Your academic record is excellent.</em>');
    };

    const quillStyles = {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '.ql-container': {
            flexGrow: 1,
            overflowY: 'auto',
            borderBottomLeftRadius: '4px',
            borderBottomRightRadius: '4px',
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : '#ccc',
        },
        '.ql-toolbar': {
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px',
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#f8f9fa',
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : '#ccc',
            '& .ql-stroke': { stroke: theme.palette.text.primary },
            '& .ql-fill': { fill: theme.palette.text.primary },
            '& .ql-picker': { color: theme.palette.text.primary },
            '& .ql-picker-options': { backgroundColor: theme.palette.background.paper }
        },
        '.ql-editor': { minHeight: '200px' }
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4" fontWeight="bold">Communication Templates</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModal()}>
                    Create Template
                </Button>
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Manage reusable email templates. {isSuperUser ? 'SuperUsers can view and duplicate templates across all companies.' : ''}
            </Typography>

            {/* Search Bar */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <TextField 
                    fullWidth size="small" placeholder="Search templates by name or company..."
                    value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon/></InputAdornment> }}
                />
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            
             {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box> : 
                Object.entries(groupedTemplates).map(([groupKey, groupTemplates]) => {
                    // Determine if this is a System Group or Company Group for styling
                    const isSystemGroup = groupKey.includes('System Templates');

                    return (
                        <Accordion key={groupKey} defaultExpanded sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }} elevation={0}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'action.selected' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    {isSystemGroup ? (
                                        <PublicIcon color="secondary" sx={{ mr: 2 }} />
                                    ) : (
                                        <BusinessIcon color="primary" sx={{ mr: 2 }} />
                                    )}
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', flexGrow: 1, color: isSystemGroup ? 'text.secondary' : 'text.primary' }}>
                                        {groupKey}
                                    </Typography>
                                    <Chip label={`${groupTemplates.length}`} size="small" sx={{ mr: 2 }} />
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                                <List disablePadding>
                                    {groupTemplates.map((template, index) => (
                                        <React.Fragment key={template.Id}>
                                            <ListItem sx={{ 
                                                '&:hover': { bgcolor: 'action.hover' }, 
                                                flexDirection: { xs: 'column', sm: 'row' }, 
                                                alignItems: { xs: 'flex-start', sm: 'center' }
                                            }}>
                                                <ListItemText 
                                                    primary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                            <Typography variant="subtitle1" fontWeight="bold">{template.TemplateName}</Typography>
                                                            {!template.CompanyId && <Chip label="Global" size="small" color="secondary" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />}
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <React.Fragment>
                                                            <Typography component="span" variant="body2" color="text.primary">Subject: {template.Subject}</Typography>
                                                            <br />
                                                            <Typography component="span" variant="caption" color="text.secondary">
                                                                {/* Show creator explicitly if needed */}
                                                                {isSuperUser && `Created by: ${template.CreatorName || 'Unknown'} • `} 
                                                                Last Updated: {new Date().toLocaleDateString()}
                                                            </Typography>
                                                        </React.Fragment>
                                                    }
                                                    sx={{ flexGrow: 1, pr: 2 }}
                                                />
                                                <Box sx={{ display: 'flex', gap: 1, mt: { xs: 1, sm: 0 } }}>
                                                    {isSuperUser && (
                                                        <Tooltip title="Duplicate to another company">
                                                            <IconButton size="small" onClick={() => { setSelectedTemplate(template); setIsDuplicateModalOpen(true); }}>
                                                                <ContentCopyIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" onClick={() => handleOpenModal(template)} color="primary">
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" onClick={() => handleOpenDeleteConfirm(template)} color="error">
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </ListItem>
                                            {index < groupTemplates.length - 1 && <Divider />}
                                        </React.Fragment>
                                    ))}
                                </List>
                            </AccordionDetails>
                        </Accordion>
                    );
                })
            }

            {/* Add/Edit Template Modal */}
            <Dialog open={isModalOpen} onClose={handleCloseModal} fullWidth maxWidth="md">
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {selectedTemplate?.Id ? 'Edit Template' : 'Create New Template'}
                    <Tabs value={editTab} onChange={(_, v) => setEditTab(v)} textColor="primary" indicatorColor="primary" size="small">
                        <Tab icon={<CodeIcon />} label="Editor" iconPosition="start" />
                        <Tab icon={<VisibilityIcon />} label="Preview" iconPosition="start" />
                    </Tabs>
                </DialogTitle>
                
                <DialogContent dividers sx={{ minHeight: '450px', display: 'flex', flexDirection: 'column' }}>
                    {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
                    
                    <TextField margin="dense" label="Template Name" fullWidth size="small" value={selectedTemplate?.TemplateName || ''} onChange={(e) => setSelectedTemplate(prev => ({ ...prev, TemplateName: e.target.value }))} disabled={isSubmitting} sx={{ mb: 2 }} />
                    <TextField margin="dense" label="Email Subject" fullWidth size="small" value={selectedTemplate?.Subject || ''} onChange={(e) => setSelectedTemplate(prev => ({ ...prev, Subject: e.target.value }))} disabled={isSubmitting} helperText="You can use variables like {{BursaryName}} in the subject." sx={{ mb: 2 }} />

                    {editTab === 0 && (
                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                {AVAILABLE_VARIABLES.map(v => (
                                    <Chip key={v.code} label={v.label} size="small" icon={<ContentCopyIcon style={{fontSize: 14}}/>} onClick={() => insertVariable(v.code)} clickable color="primary" variant="outlined" />
                                ))}
                            </Box>
                            <Box sx={{ flexGrow: 1, ...quillStyles }}>
                                <ReactQuill ref={quillRef} theme="snow" value={selectedTemplate?.Body || ''} onChange={(content) => setSelectedTemplate(prev => ({ ...prev, Body: content }))} style={{ height: '300px' }} />
                            </Box>
                        </Box>
                    )}

                    {editTab === 1 && (
                        <Box sx={{ flexGrow: 1, p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default', overflowY: 'auto' }}>
                            <div className="preview-content" dangerouslySetInnerHTML={{ __html: getPreviewHtml(selectedTemplate?.Body) }} />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} disabled={isSubmitting}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" disabled={isSubmitting}>Save Template</Button>
                </DialogActions>
            </Dialog>

            <DeleteConfirmModal open={isDeleteModalOpen} onClose={handleCloseDeleteConfirm} onConfirm={handleConfirmDelete} templateName={selectedTemplate?.TemplateName || ''} />
            <DuplicateConfirmModal open={isDuplicateModalOpen} onClose={handleCloseDuplicateModal} onConfirm={handleConfirmDuplicate} templateName={selectedTemplate?.TemplateName || ''} companies={companies} />
        </Container>
    );
};

export default TemplateManagerPage;